<template>
    <div v-if="modelValue" class="preview-takeover">
        <!-- Column -->
        <div class="column">
            <div class="top">
                <!-- Copy -->
                <h1>An all-new Opteo</h1>
                <p>
                    The official handover to the all-new Opteo is happening soon. On July 1st 2026,
                    the current version of Opteo will be moved to <b>old.opteo.com</b> and replaced
                    with the new app. All of your accounts, settings, and preferences will be
                    carried over automatically—everything in your Opteo account will continue to
                    behave as it does today.
                </p>
                <p>
                    If you'd prefer to get ahead of the switch, you can head to
                    <b>preview.opteo.com</b> and log in using your email and password. With new
                    additions like <b>Account Manager</b> and a completely revamped <b>Chat</b>,
                    there are tons of new features to explore and start using today.
                </p>

                <!-- Link -->
                <div class="link nested">
                    <span>preview.opteo.com</span>
                    <button @click="openPreview">
                        Open in New Tab
                        <IconUpRightArrow />
                    </button>
                </div>
            </div>

            <!-- Mockup -->
            <img
                class="mockup mobile"
                src="@/assets/img/chat-mockup.png"
                @load="onImageLoaded"
                @error="onImageLoaded"
            />

            <!-- FAQ -->
            <div class="nested">
                <PreviewTakeoverFAQ :items="faqItems" />
            </div>
        </div>

        <!-- Mockup -->
        <img
            ref="mockupImg"
            class="mockup"
            src="@/assets/img/chat-mockup.png"
            @load="onImageLoaded"
            @error="onImageLoaded"
        />

        <!-- Close Button -->
        <button class="close" @click="close">
            <IconClose />
        </button>
    </div>
</template>

<script setup lang="ts">
// Imports
import { ref, onMounted, watch } from 'vue'
import anime from 'animejs/lib/anime.es.js'
// import { useLayouts } from '@/composition/layouts/useLayouts'

// Components
import IconClose from './IconClose.vue'
import IconUpRightArrow from './IconUpRightArrow.vue'
import PreviewTakeoverFAQ, { type FaqItem } from './PreviewTakeoverFAQ.vue'

// useLayouts('full-screen')

// v-model —> controls visibility from the parent (pages/user/index.vue)
defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

function close() {
    emit('update:modelValue', false)
}

// Load gating —> hide the column + mockup until both the variable fonts and the mockup image
// are ready, then fade them in. Prevents FOUT (fonts swapping mid-paint) and image jank.
const fontsLoaded = ref(false)
const imagesLoaded = ref(false)
const mockupImg = ref<HTMLImageElement | null>(null)

function onImageLoaded() {
    imagesLoaded.value = true
}

onMounted(() => {
    // Pre-hide both targets synchronously so there is no flash before the reveal animation.
    anime.set(['.preview-takeover .column', '.preview-takeover .mockup'], { opacity: 0 })

    // Fonts —> resolves once the browser has settled all in-flight font loads.
    document.fonts.ready.then(() => {
        fontsLoaded.value = true
    })

    // Cached-image guard —> a @load event may not fire for an already-cached image.
    if (mockupImg.value?.complete) {
        onImageLoaded()
    }
})

// Reveal once both fonts and the image are ready, with a 400ms stagger between targets.
watch([fontsLoaded, imagesLoaded], ([f, i]) => {
    if (f && i) {
        anime.timeline().add({
            targets: ['.preview-takeover .column', '.preview-takeover .mockup'],
            opacity: [0, 1],
            delay: anime.stagger(400),
            duration: 700,
            easing: 'easeOutCubic',
        })
    }
})

function openPreview() {
    // ?ref=switch lets the new app attribute arrivals to the takeover banner.
    window.open('https://preview.opteo.com/?ref=switch', '_blank', 'noopener,noreferrer')
}

// FAQ content
const faqItems: FaqItem[] = [
    {
        query: 'What new features are available?',
        response:
            'New features like Account Groups, Group Performance, and Account Manager are available now. Core features like Chat, Improvements, and Performance come with dozens of updates and improved functionality.',
    },
    {
        query: 'Are any features being deprecated?',
        response: `The only feature that won't find an immediate parallel in the new app is Reports. For Reports users—the existing feature will continue to be available via {old.opteo.com} until the new version of Reports is available.`,
    },
    {
        query: 'Do I have to change or update anything?',
        response:
            'Not at all, just head to {preview.opteo.com} and log in using your existing email address and password—all of your accounts, settings, and preferences will be carried over automatically, everything in your Opteo account will continue to behave as it does today.',
    },
    {
        query: 'Will my subscription change?',
        response: `No, your subscription stays exactly the same. All of your information is carried over automatically when moving to the new app—there's nothing you need to do, and nothing will change on your next invoice.`,
    },
]
</script>

<style scoped lang="scss">
// Mixins
@mixin squircle($radius) {
    border-radius: $radius;

    @supports (corner-shape: superellipse(1.44)) {
        corner-shape: superellipse(1.44);
        border-radius: calc(#{$radius} * 1.36);
    }
}

// Button
button {
    all: unset;
    cursor: pointer;
    will-change: transform;
    transition:
        background-color 0.12s ease,
        transform 0.08s ease;
}
button:active {
    transform: scale(0.98);
}

// Container
.preview-takeover {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: hsl(240, 3%, 7%);
    z-index: 2147483647;
    display: flex;
    align-items: flex-start;
    gap: 88px;
    overflow: hidden;
    overscroll-behavior: none;
}
.preview-takeover::-webkit-scrollbar {
    display: none !important;
}

// Column
.column {
    width: 332px;
    min-height: 100%;
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    justify-content: space-between;
    gap: 48px;
    color: white;
    font-family: 'Inter Variable';
    font-feature-settings:
        'cv01' on,
        'cv11' on,
        'cv10' off,
        'ss01' off,
        'ss03' on;
}
.column .top {
    flex-shrink: 0;
}

// Content
.column h1 {
    font-size: 35.74px;
    line-height: 42px;
    letter-spacing: 0;
    font-variation-settings:
        'wght' 560,
        'opsz' 32;
    margin-bottom: 25px;
}
.column p {
    color: hsl(240, 3%, 60%);
    font-size: 12.37px;
    line-height: 20px;
    letter-spacing: 0;
    word-spacing: -0.2px;
    font-variation-settings:
        'wght' 436,
        'opsz' 14;
    margin-bottom: 16px;
}
.column p + p {
    margin-bottom: 25px;
}
.column p b {
    background-color: hsl(240, 5%, 12%);
    @include squircle(5px);
    padding: 0 3px;
    font-variation-settings:
        'wght' 540,
        'opsz' 14;
}

// Link
.column .link {
    @include squircle(15px);
    background-color: black;
    padding: 5px 5px 5px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.column .link span {
    font-family: 'Aeonik Mono VF';
    font-size: 12.85px;
    padding-bottom: 2px;
}
.column .link button {
    height: 30px;
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 0 11px;
    background-color: hsl(240, 5%, 10%);
    @include squircle(12px);
    font-size: 11px;
    line-height: 14px;
    font-variation-settings:
        'wght' 500,
        'opsz' 14;
}
.column .link button:hover {
    background-color: hsl(240, 5%, 13%);
}
.column .link button svg {
    opacity: 0.88;
}

// Nested
.nested {
    max-width: 330px;
}

// Mockup
.mockup {
    min-width: 1364px;
    max-width: 1364px;
    min-height: 1218px;
    max-height: 1218px;
    transform: translateY(-24px);
    flex-shrink: 0;
}
.mockup.mobile {
    display: none;
}

// Close
button.close {
    position: fixed;
    top: 24px;
    right: 24px;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid hsl(240, 8%, 22%);
    background-blend-mode: overlay, normal, normal;
    background:
        url('@/assets/img/noise.png') repeat,
        radial-gradient(200% 100% at 50% 0%, hsl(240, 8%, 22%) 0%, transparent 100%) no-repeat,
        hsl(240, 7%, 18%) no-repeat;
    background-size: 2rem, cover, cover;
    color: #ffffff;
}
button.close:active {
    transform: scale(0.95);
}

// Responsive
@media screen and (max-width: 1119px) {
    .preview-takeover {
        padding: 48px 12px;
        overflow: auto;
    }
    .column {
        margin: 0 auto;
        min-height: unset;
    }
    .mockup.mobile {
        display: block;
        min-width: unset;
        min-height: unset;
        width: 100%;
        margin-top: calc(18px + 8px);
        margin-bottom: 18px;
        transform: translateY(0) scale(1.25);
    }
    .mockup {
        display: none;
    }
    .column h1 {
        font-size: 27.49px;
        line-height: 36px;
        font-variation-settings:
            'wght' 560,
            'opsz' 28;
        margin-bottom: 19px;
    }
    .column p {
        margin-bottom: 18px;
    }
    .column p + p {
        margin-bottom: 23px;
    }
}
@media screen and (min-width: 1120px) and (max-width: 1280px) {
    .preview-takeover {
        padding: 30px 36px;
    }
    .column h1 {
        font-size: 27.49px;
        line-height: 36px;
        font-variation-settings:
            'wght' 560,
            'opsz' 28;
        margin-bottom: 19px;
    }
    .column p {
        margin-bottom: 18px;
    }
    .column p + p {
        margin-bottom: 23px;
    }
    .mockup {
        min-width: 1184px;
        min-height: unset;
    }
}
@media screen and (min-width: 1281px) and (max-width: 1720px) {
    .preview-takeover {
        padding: 64px;
    }
}
@media screen and (min-width: 1721px) and (max-width: 1919px) {
    .preview-takeover {
        padding: 64px;
        gap: 160px;
    }
}
@media screen and (min-width: 1920px) {
    .preview-takeover {
        padding: 64px;
        gap: 192px;
    }
    .mockup {
        transform: translateY(0);
    }
}
</style>
